import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Users, Dumbbell, Sparkles, Package, CalendarClock,
  Activity, Ruler, UserPlus, CreditCard, Receipt, BarChart3, Building2, Settings,
  LogOut, Menu, X, ChevronRight, Scale, ListChecks,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { label: "Kontrol Paneli", to: "/admin", icon: LayoutDashboard, end: true },
  { label: "Üyeler", to: "/admin/members", icon: Users },
  { label: "Antrenörler", to: "/admin/trainers", icon: Dumbbell },
  { label: "Hizmetler", to: "/admin/services", icon: Sparkles },
  { label: "Paketler", to: "/admin/packages", icon: Package },
  { label: "Şubeler", to: "/admin/branches", icon: Building2, fullAdminOnly: true },
  { label: "Takvim", to: "/admin/calendar", icon: Calendar },
  { label: "Bekleme Listesi", to: "/admin/waitlist", icon: ListChecks },
  { label: "Ölçümler", to: "/admin/measurements", icon: Ruler },
  { label: "CRM & Adaylar", to: "/admin/crm", icon: UserPlus },
  { label: "Antrenmanlar", to: "/admin/workouts", icon: Activity, phase: 3 },
  { label: "Ödemeler", to: "/admin/payments", icon: CreditCard },
  { label: "Giderler", to: "/admin/expenses", icon: Receipt },
  { label: "Raporlar", to: "/admin/reports", icon: BarChart3 },
  { label: "Şube Karşılaştırma", to: "/admin/branch-comparison", icon: Scale, fullAdminOnly: true },
  { label: "Ayarlar", to: "/admin/settings", icon: Settings, phase: 6, fullAdminOnly: true },
];

const FULL_ADMIN = ["admin", "super_admin", "franchise_owner"];

function NavItems({ onNavigate, role }) {
  const items = NAV.filter((i) => !i.fullAdminOnly || FULL_ADMIN.includes(role));
  return (
    <nav className="flex flex-col gap-1 px-3" data-testid="admin-sidebar-navigation">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${item.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-[#FF5500] text-white shadow-[0_4px_16px_rgba(255,85,0,0.35)]"
                  : "text-[#A0A0B2] hover:text-white hover:bg-[#1B1B27]"
              }`
            }
          >
            <Icon size={18} strokeWidth={2.1} />
            <span className="flex-1">{item.label}</span>
            {item.phase && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#F59E0B]/15 text-[#F59E0B]">
                F{item.phase}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [branchLabel, setBranchLabel] = useState("");
  const navigate = useNavigate();
  const initials = (user?.name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("");
  const isBranchManager = user?.role === "branch_manager";
  const roleLabel = isBranchManager ? "Şube Yöneticisi" : "Yönetici";

  useEffect(() => {
    api.get("/branches").then((r) => {
      const list = r.data || [];
      if (isBranchManager) {
        const b = list.find((x) => x.id === user?.branch_id) || list[0];
        setBranchLabel(b ? b.name.replace(/^Get Up Fit\s*/i, "") : "Şubem");
      } else {
        setBranchLabel("Tüm Şubeler");
      }
    }).catch(() => setBranchLabel(""));
  }, [isBranchManager, user?.branch_id]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0B0E] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 bg-[#0E0E14] border-r border-[#1A1A24] z-30">
        <div className="h-16 flex items-center px-5 border-b border-[#1A1A24]">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavItems role={user?.role} />
        </div>
        <div className="p-3 border-t border-[#1A1A24]">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9"><AvatarFallback className="bg-[#FF5500] text-white text-xs font-bold">{initials}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-[#68687B] truncate">{roleLabel}</p>
            </div>
            <button onClick={doLogout} data-testid="admin-logout-button" className="p-2 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#1B1B27] transition-colors">
              <LogOut size={17} />
            </button>
          </div>
          <ChangePasswordDialog trigger={<button data-testid="admin-change-password" className="w-full text-left text-xs text-[#68687B] hover:text-[#FF5500] px-2 pb-1 transition-colors">Şifre Değiştir</button>} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#0E0E14] border-r border-[#1A1A24] flex flex-col animate-fade-up">
            <div className="h-16 flex items-center justify-between px-5 border-b border-[#1A1A24]">
              <Logo />
              <button onClick={() => setOpen(false)} className="p-2 text-[#A0A0B2]"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto py-4"><NavItems role={user?.role} onNavigate={() => setOpen(false)} /></div>
            <div className="p-3 border-t border-[#1A1A24]">
              <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 w-full rounded-xl text-sm text-[#EF4444] hover:bg-[#1B1B27]">
                <LogOut size={18} /> Çıkış Yap
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-[#0B0B0E]/85 backdrop-blur-xl border-b border-[#1A1A24] flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-lg text-white hover:bg-[#1B1B27]" data-testid="admin-menu-button">
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2 text-sm" data-testid="admin-branch-context">
              <span className="text-[#68687B]">Get Up Fit</span>
              <ChevronRight size={14} className="text-[#68687B]" />
              <span className="text-white font-medium">{branchLabel || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell role={user?.role} />
            <div className="flex items-center gap-2 text-xs font-mono text-[#68687B]">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Canlı
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 animate-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
